// pages/Personal Attendance/MyAttendanceSchedule.js
//
// Legacy menus:
//   "Show My FTO Schedule"  → kind="FTO"
//   "Show My COA Schedule"  → kind="COA"
//
// Owner 2026-10-30: "same UI as Google Calendar, with 1 week, 1 month etc."
// One calendar, three views: Day and Week are a time grid (all-day band on top,
// hours below, red line at the current time), Month is the calm grid. The
// person's own records (via /dept?mine=true) are the solid chips; the Google
// calendars (own meetings, company events, colleagues' time off from the SSW
// Attendance calendar) are soft chips behind the Show pills.
//
// One component, two exports — keeps the layout and styling in one
// place so the FTO and COA versions stay visually identical.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Container, Card, CardBody, Button, ButtonGroup, Alert } from "reactstrap";
import { DashboardPageHeader, TableSkeleton } from "../../Components/Common/DashboardKit";
import { getFtoDept, getCoaDept, getMyCalendar } from "./ftoService";

// ── date helpers ─────────────────────────────────────────────────────────────
const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const pad2 = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const fromYmd = (s) => { const [y, m, d] = String(s).slice(0, 10).split("-").map(Number); return new Date(y, m - 1, d); };
const dayOnly = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d, n) => { const x = dayOnly(d); x.setDate(x.getDate() + n); return x; };
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const startOfWeek = (d) => addDays(d, -d.getDay());
const dayDiff = (a, b) => Math.round((dayOnly(a) - dayOnly(b)) / 86400000);
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const minutesOf = (d) => d.getHours() * 60 + d.getMinutes();
const fmtMin = (mins) => {
    const h = Math.floor(mins / 60) % 24, m = mins % 60;
    const ap = h >= 12 ? "pm" : "am";
    const hh = ((h + 11) % 12) + 1;
    return m ? `${hh}:${pad2(m)}${ap}` : `${hh}${ap}`;
};
const hourLabel = (h) => h === 0 ? "" : h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h - 12} PM`;
const hhmmToMin = (s) => {
    const m = /^(\d{1,2}):(\d{2})/.exec(String(s || "").trim());
    return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
const shortDate = (d, withYear) => d.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(withYear ? { year: "numeric" } : {}) });

// ── colours (theme tokens with Light Classic fallbacks) ──────────────────────
const PALETTE = {
    own:        { bg: "var(--ssw-primary-fill, #405189)", fg: "#fff", bar: "transparent" },
    mine:       { bg: "var(--ssw-tint-blue, #dce4f5)",   fg: "var(--ssw-ink-blue, #0c447c)",   bar: "var(--ssw-ink-blue, #6a86c8)" },
    company:    { bg: "var(--ssw-tint-green, #d1f2e4)",  fg: "var(--ssw-ink-green, #0a3622)",  bar: "var(--ssw-ink-green, #2f9e6e)" },
    attendance: { bg: "var(--ssw-tint-gray, #e9ebee)",   fg: "var(--ssw-fg-1, #495057)",       bar: "var(--ssw-fg-3, #adb5bd)" },
};
const chipBase = (cal, timed) => ({
    background: PALETTE[cal].bg, color: PALETTE[cal].fg,
    borderLeft: timed ? `3px solid ${PALETTE[cal].bar}` : undefined,
    borderRadius: 4, fontSize: 11, lineHeight: 1.3, padding: "2px 5px",
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left", textDecoration: "none", display: "block",
});

// ── normalise everything into one item shape ─────────────────────────────────
// { key, cal: own|mine|company|attendance, title, sub, allDay, startDay, endDay, startMin, endMin, link, location }
const googleItems = (gcal) => {
    const out = [];
    for (const ev of gcal?.items || []) {
        const cal = PALETTE[ev.calendar] ? ev.calendar : "company";
        if (ev.allDay) {
            const s = fromYmd(ev.start);
            let e = ev.end ? addDays(fromYmd(ev.end), -1) : s;      // Google's end is exclusive
            if (e < s) e = s;
            out.push({ key: `g${ev.id}`, cal, title: ev.title, sub: ev.calendarName, allDay: true, startDay: ymd(s), endDay: ymd(e), link: ev.link, location: ev.location });
            continue;
        }
        const s = new Date(ev.start), e = new Date(ev.end || ev.start);
        if (isNaN(s)) continue;
        const endSame = !isNaN(e) && sameDay(s, e);
        if (!isNaN(e) && !endSame && e - s >= 86400000) {          // a timed event that lasts a day or more: show as all-day
            out.push({ key: `g${ev.id}`, cal, title: ev.title, sub: ev.calendarName, allDay: true, startDay: ymd(s), endDay: ymd(e), link: ev.link, location: ev.location });
            continue;
        }
        const startMin = minutesOf(s);
        const endMin = endSame ? Math.max(minutesOf(e), startMin + 15) : 24 * 60;
        out.push({ key: `g${ev.id}`, cal, title: ev.title, sub: ev.calendarName, allDay: false, startDay: ymd(s), endDay: ymd(s), startMin, endMin, link: ev.link, location: ev.location });
    }
    return out;
};
const approvalWord = (r) => r.closedUnreviewed ? "not reviewed" : (r.supervisorApproval === true && r.hrApproval === true) ? "" : (r.supervisorApproval === false || r.hrApproval === false) ? "denied" : "pending";
const ownItems = (rows, kind) => {
    const out = [];
    for (const r of rows) {
        if (!r.startDate) continue;
        const startDay = String(r.startDate).slice(0, 10);
        const endDay = r.endDate ? String(r.endDate).slice(0, 10) : startDay;
        const status = approvalWord(r);
        if (kind === "COA") {
            const sm = hhmmToMin(r.modifiedStartTime), em = hhmmToMin(r.modifiedEndTime);
            if (sm != null && em != null && em > sm) {
                // A COA stores the hours the person WILL work that day; the block covers
                // those hours and says so (Google shows the time away instead).
                const title = `My COA · working ${fmtMin(sm)} – ${fmtMin(em)}${status ? ` · ${status}` : ""}`;
                for (let d = fromYmd(startDay), i = 0; d <= fromYmd(endDay) && i < 62; d = addDays(d, 1), i++) {
                    out.push({ key: `c${r.id}-${i}`, cal: "own", title, short: "My COA", sub: r.employeeComment || "", allDay: false, startDay: ymd(d), endDay: ymd(d), startMin: sm, endMin: em });
                }
            } else {
                out.push({ key: `c${r.id}`, cal: "own", title: `My COA${status ? ` · ${status}` : ""}`, sub: r.employeeComment || "", allDay: true, startDay, endDay });
            }
            continue;
        }
        const bits = ["My FTO"];
        const type = String(r.typeOfLeave || "").trim().toUpperCase();
        if (type === "COMPANY") bits.push("company business");
        else if (type === "OTHER") bits.push("other leave");
        if (r.payStatus === "Unpaid") bits.push("unpaid");
        if (status) bits.push(status);
        out.push({ key: `f${r.id}`, cal: "own", title: bits.join(" · "), sub: r.ftoCount ? `${r.ftoCount} day${r.ftoCount === 1 ? "" : "s"}` : "", allDay: true, startDay, endDay });
    }
    return out;
};

// ── layout helpers ───────────────────────────────────────────────────────────
// All-day bars across the columns of a week (or a single day). Greedy lanes.
const allDayLanes = (items, first, count) => {
    const bars = [];
    for (const it of items) {
        const s = Math.max(0, dayDiff(fromYmd(it.startDay), first));
        const e = Math.min(count - 1, dayDiff(fromYmd(it.endDay), first));
        if (e >= s) bars.push({ it, s, e, lane: 0 });
    }
    bars.sort((a, b) => a.s - b.s || (b.e - b.s) - (a.e - a.s) || a.it.title.localeCompare(b.it.title));
    const laneEnd = [];
    for (const b of bars) {
        let l = laneEnd.findIndex((end) => end < b.s);
        if (l < 0) { l = laneEnd.length; laneEnd.push(-1); }
        laneEnd[l] = b.e; b.lane = l;
    }
    return { bars, lanes: laneEnd.length };
};
// Timed blocks of one day: overlapping blocks share the width (Google's columns).
// More than `maxLanes` side by side would be unreadable slivers in a week column, so
// such a cluster becomes ONE block ("N at the same time") that opens the day.
const timedLayout = (items, maxLanes = 99) => {
    const sorted = [...items].sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin);
    const placed = [];
    let cluster = [], clusterEnd = -1;
    const flush = () => {
        if (!cluster.length) return;
        if (cluster.length > 1) {
            // how many are truly side by side at the busiest minute?
            const lanesNeeded = (() => {
                const le = [];
                for (const ev of cluster) {
                    let l = le.findIndex((end) => end <= ev.startMin);
                    if (l < 0) { l = le.length; le.push(0); }
                    le[l] = ev.endMin;
                }
                return le.length;
            })();
            if (lanesNeeded > maxLanes) {
                placed.push({
                    group: cluster.map((e) => e.it),
                    startMin: Math.min(...cluster.map((e) => e.startMin)),
                    endMin: Math.max(...cluster.map((e) => e.endMin)),
                    lane: 0, lanes: 1, span: 1,
                });
                cluster = [];
                return;
            }
        }
        const laneEnd = [];
        for (const ev of cluster) {
            let l = laneEnd.findIndex((end) => end <= ev.startMin);
            if (l < 0) { l = laneEnd.length; laneEnd.push(0); }
            laneEnd[l] = ev.endMin; ev.lane = l;
        }
        // Google's rule: a block spreads to the right over the lanes nothing else uses
        // at that time, so only truly simultaneous items share the width.
        const overlaps = (a, b) => a.startMin < b.endMin && b.startMin < a.endMin;
        for (const ev of cluster) {
            ev.lanes = laneEnd.length;
            ev.span = 1;
            for (let l = ev.lane + 1; l < laneEnd.length; l++) {
                if (cluster.some((o) => o.lane === l && overlaps(o, ev))) break;
                ev.span++;
            }
        }
        placed.push(...cluster); cluster = [];
    };
    for (const it of sorted) {
        const ev = { it, startMin: it.startMin, endMin: Math.max(it.endMin, it.startMin + 30), lane: 0, lanes: 1, span: 1 };
        if (cluster.length && ev.startMin >= clusterEnd) { flush(); clusterEnd = -1; }
        cluster.push(ev); clusterEnd = Math.max(clusterEnd, ev.endMin);
    }
    flush();
    return placed;
};

const HOUR = 48;          // px per hour in the time grid
const GUTTER = 56;        // px for the hour labels
const SCROLL_TO = 7;      // first hour in view

// dense = a narrow block in a crowded hour: title only, smaller type; the time is in the tooltip
const Chip = ({ it, style, timed, dense }) => {
    const time = it.allDay ? "" : `${fmtMin(it.startMin)} – ${fmtMin(it.endMin)}`;
    const tip = [it.sub && it.cal !== "own" ? it.sub : null, it.title, time || "All day", it.location].filter(Boolean).join("\n");
    const props = it.link ? { href: it.link, target: "_blank", rel: "noopener noreferrer" } : {};
    const Tag = it.link ? "a" : "div";
    return (
        <Tag {...props} title={tip} style={{ ...chipBase(it.cal, timed), ...(dense ? { fontSize: 10, padding: "1px 3px" } : {}), ...style }}>
            {timed && !dense && <span style={{ opacity: 0.85, marginRight: 4 }}>{fmtMin(it.startMin)}</span>}
            {dense ? (it.short || it.title) : it.title}
        </Tag>
    );
};

const Pill = ({ on, tone, onClick, children }) => (
    <button type="button" onClick={onClick}
        style={{
            borderRadius: 999, padding: "2px 10px", fontSize: 12, lineHeight: "18px", cursor: "pointer",
            background: on ? PALETTE[tone].bg : "transparent",
            color: on ? PALETTE[tone].fg : "var(--ssw-fg-2, #6b7280)",
            border: `1px solid ${on ? "transparent" : "var(--ssw-line, #ced4da)"}`,
        }}>
        <i className={`me-1 ${on ? "ri-checkbox-circle-fill" : "ri-checkbox-blank-circle-line"}`} />{children}
    </button>
);

// ── the page ─────────────────────────────────────────────────────────────────
const ScheduleCalendar = ({ kind, fetcher, title, breadcrumb }) => {
    const [today, setToday] = useState(() => new Date());
    const [mode, setMode] = useState(() => {
        try { const v = localStorage.getItem("ssw:mySchedule:mode"); return ["day", "week", "month"].includes(v) ? v : "week"; }
        catch { return "week"; }
    });
    const [cursor, setCursor] = useState(() => dayOnly(new Date()));
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [gcal, setGcal] = useState(null);        // null = off or not loaded yet
    const [gcalLoading, setGcalLoading] = useState(false);
    const [show, setShow] = useState(() => {
        const def = { mine: true, company: false, out: true };
        try { return { ...def, ...JSON.parse(localStorage.getItem("ssw:mySchedule:show") || "{}") }; }
        catch { return def; }
    });
    const scrollRef = useRef(null);

    const pickMode = (m) => { setMode(m); try { localStorage.setItem("ssw:mySchedule:mode", m); } catch { /* ignore */ } };
    const toggle = (k) => setShow((s) => {
        const next = { ...s, [k]: !s[k] };
        try { localStorage.setItem("ssw:mySchedule:show", JSON.stringify(next)); } catch { /* ignore */ }
        return next;
    });

    // tick once a minute so the red line moves and "today" rolls over
    useEffect(() => { const t = setInterval(() => setToday(new Date()), 60000); return () => clearInterval(t); }, []);

    // visible range
    const range = useMemo(() => {
        if (mode === "month") return { from: new Date(cursor.getFullYear(), cursor.getMonth(), 1), to: new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0) };
        if (mode === "week") { const f = startOfWeek(cursor); return { from: f, to: addDays(f, 6) }; }
        return { from: dayOnly(cursor), to: dayOnly(cursor) };
    }, [mode, cursor]);
    const days = useMemo(() => {
        const out = [];
        for (let d = range.from; d <= range.to; d = addDays(d, 1)) out.push(d);
        return out;
    }, [range]);
    const fromKey = ymd(range.from), toKey = ymd(range.to);

    // Google calendars for the visible range
    useEffect(() => {
        let alive = true;
        setGcalLoading(true);
        getMyCalendar(fromKey, toKey)
            .then(({ data }) => { if (alive) setGcal(data && data.enabled ? data : null); })
            .catch(() => { if (alive) setGcal(null); })
            .finally(() => { if (alive) setGcalLoading(false); });
        return () => { alive = false; };
    }, [fromKey, toKey]);

    // own records: one call per calendar year the range touches
    const yearsKey = range.from.getFullYear() === range.to.getFullYear() ? String(range.from.getFullYear()) : `${range.from.getFullYear()},${range.to.getFullYear()}`;
    useEffect(() => {
        let alive = true;
        setLoading(true); setError(null);
        const years = yearsKey.split(",").map(Number);
        Promise.all(years.map((year) => fetcher({ mine: true, year })))
            .then((results) => {
                if (!alive) return;
                const seen = new Set(); const all = [];
                for (const { data } of results) {
                    for (const r of (Array.isArray(data) ? data : (data?.items ?? []))) {
                        if (r && !seen.has(r.id)) { seen.add(r.id); all.push(r); }
                    }
                }
                setRows(all);
            })
            .catch((e) => { if (alive) setError(e?.response?.data?.error || e?.message || "Failed to load"); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [yearsKey, fetcher]);

    // everything visible, after the Show pills
    const items = useMemo(() => {
        const g = googleItems(gcal).filter((it) => it.cal === "mine" ? show.mine : it.cal === "company" ? show.company : show.out);
        return [...ownItems(rows, kind), ...g].filter((it) => it.endDay >= fromKey && it.startDay <= toKey);
    }, [gcal, rows, kind, show, fromKey, toKey]);
    const outByDay = useMemo(() => {
        const m = new Map();
        for (const it of googleItems(gcal)) {
            if (it.cal !== "attendance") continue;
            for (let d = fromYmd(it.startDay), i = 0; d <= fromYmd(it.endDay) && i < 62; d = addDays(d, 1), i++) {
                const k = ymd(d); m.set(k, (m.get(k) || 0) + 1);
            }
        }
        return m;
    }, [gcal]);
    const byDay = useMemo(() => {
        const m = new Map();
        for (const it of items) {
            for (let d = fromYmd(it.startDay), i = 0; d <= fromYmd(it.endDay) && i < 62; d = addDays(d, 1), i++) {
                const k = ymd(d);
                if (!m.has(k)) m.set(k, []);
                m.get(k).push(it);
            }
        }
        for (const list of m.values()) list.sort((a, b) => (a.allDay ? -1 : a.startMin) - (b.allDay ? -1 : b.startMin) || (a.cal === "own" ? -1 : 0) - (b.cal === "own" ? -1 : 0));
        return m;
    }, [items]);

    // scroll the time grid to the morning whenever it (re)appears
    useEffect(() => {
        if (mode !== "month" && scrollRef.current) scrollRef.current.scrollTop = SCROLL_TO * HOUR - 6;
    }, [mode, fromKey]);

    const go = (n) => setCursor((c) => mode === "month" ? addMonths(c, n) : addDays(c, mode === "week" ? 7 * n : n));
    const openDay = (d) => { setCursor(dayOnly(d)); pickMode("day"); };

    const heading = (() => {
        if (mode === "month") return `${MONTH_NAMES[cursor.getMonth()]} ${cursor.getFullYear()}`;
        if (mode === "day") return cursor.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
        const f = range.from, t = range.to;
        if (f.getFullYear() !== t.getFullYear()) return `${shortDate(f, true)} – ${shortDate(t, true)}`;
        if (f.getMonth() !== t.getMonth()) return `${shortDate(f)} – ${shortDate(t)}, ${t.getFullYear()}`;
        return `${shortDate(f)} – ${t.getDate()}, ${t.getFullYear()}`;
    })();
    const isCurrent = mode === "month" ? (cursor.getMonth() === today.getMonth() && cursor.getFullYear() === today.getFullYear())
        : days.some((d) => sameDay(d, today));

    // ── month grid ──
    const monthCells = useMemo(() => {
        if (mode !== "month") return [];
        const out = [];
        for (let i = 0; i < range.from.getDay(); i++) out.push(null);
        for (let d = 1; d <= range.to.getDate(); d++) out.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
        while (out.length % 7 !== 0) out.push(null);
        return out;
    }, [mode, range, cursor]);

    const renderMonth = () => (
        <div className="table-responsive">
            <table className="table table-bordered mb-0 text-center" style={{ tableLayout: "fixed" }}>
                <thead className="table-light">
                    <tr>{DOW.map((d) => <th key={d} style={{ width: "14.28%", fontSize: 11, letterSpacing: ".04em", textTransform: "uppercase" }}>{d}</th>)}</tr>
                </thead>
                <tbody>
                    {loading ? <TableSkeleton rows={5} columns={7} height={60} /> : Array.from({ length: monthCells.length / 7 }).map((_, wi) => (
                        <tr key={wi}>
                            {monthCells.slice(wi * 7, wi * 7 + 7).map((cell, di) => {
                                if (!cell) return <td key={di} style={{ background: "var(--ssw-surface-3, #fafafa)" }}></td>;
                                const key = ymd(cell);
                                const list = byDay.get(key) || [];
                                const isToday = sameDay(cell, today);
                                const isWeekend = cell.getDay() === 0 || cell.getDay() === 6;
                                const outCount = show.out ? (outByDay.get(key) || 0) : 0;
                                const shown = list.filter((it) => it.cal !== "attendance");
                                const MAX = 3;
                                return (
                                    <td key={di} onClick={() => openDay(cell)} title="Open this day"
                                        style={{
                                            verticalAlign: "top", height: 96, cursor: "pointer", padding: 6,
                                            background: isWeekend ? "var(--ssw-surface-2, #fafbfc)" : undefined,
                                        }}>
                                        <div className="d-flex justify-content-between align-items-start mb-1">
                                            {outCount > 0
                                                ? <span title={`${outCount} colleague${outCount === 1 ? "" : "s"} out`}
                                                    style={{ background: PALETTE.attendance.bg, color: PALETTE.attendance.fg, fontSize: 10, borderRadius: 999, padding: "0 6px", lineHeight: "16px" }}>{outCount} out</span>
                                                : <span />}
                                            <span style={{
                                                fontSize: 12, minWidth: 22, height: 22, lineHeight: "22px", borderRadius: 11, textAlign: "center", padding: "0 4px",
                                                background: isToday ? "var(--ssw-primary-fill, #405189)" : "transparent",
                                                color: isToday ? "#fff" : "var(--ssw-fg-2, #6b7280)", fontWeight: isToday ? 600 : 400,
                                            }}>{cell.getDate()}</span>
                                        </div>
                                        {shown.slice(0, MAX).map((it) => <Chip key={it.key} it={it} timed={!it.allDay} style={{ marginTop: 2, fontSize: 10 }} />)}
                                        {shown.length > MAX && <div className="text-muted" style={{ fontSize: 10, marginTop: 2 }}>+{shown.length - MAX} more</div>}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    // ── day / week time grid ──
    const renderTimeGrid = () => {
        const count = days.length;
        const colTemplate = `${GUTTER}px repeat(${count}, minmax(0, 1fr))`;
        const allDay = allDayLanes(items.filter((it) => it.allDay), range.from, count);
        const nowMin = minutesOf(today);
        const line = "1px solid var(--ssw-line, #e9ebec)";
        return (
            <div style={{ overflowX: "auto" }}>
                <div style={{ minWidth: count === 1 ? 320 : 1000 }}>
                    {/* day headers */}
                    <div style={{ display: "grid", gridTemplateColumns: colTemplate, borderBottom: line }}>
                        <div />
                        {days.map((d) => {
                            const isToday = sameDay(d, today);
                            return (
                                <div key={ymd(d)} onClick={() => mode === "week" && openDay(d)} title={mode === "week" ? "Open this day" : undefined}
                                    style={{ textAlign: "center", padding: "6px 0 4px", cursor: mode === "week" ? "pointer" : "default", borderLeft: line }}>
                                    <div style={{ fontSize: 11, letterSpacing: ".06em", textTransform: "uppercase", color: isToday ? "var(--ssw-primary-ink, #405189)" : "var(--ssw-fg-2, #6b7280)" }}>{DOW[d.getDay()]}</div>
                                    <div style={{
                                        display: "inline-block", minWidth: 36, height: 36, lineHeight: "36px", borderRadius: 18, fontSize: 20, fontWeight: 500,
                                        background: isToday ? "var(--ssw-primary-fill, #405189)" : "transparent",
                                        color: isToday ? "#fff" : "var(--ssw-fg-1, #212529)",
                                    }}>{d.getDate()}</div>
                                </div>
                            );
                        })}
                    </div>
                    {/* all-day band */}
                    <div style={{ display: "grid", gridTemplateColumns: colTemplate, borderBottom: "2px solid var(--ssw-line, #dfe3e7)" }}>
                        <div className="text-muted" style={{ fontSize: 10, textAlign: "right", paddingRight: 6, paddingTop: 4 }}>All day</div>
                        <div style={{ gridColumn: `2 / span ${count}`, display: "grid", gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`, gridAutoRows: 22, gap: "2px 0", padding: "3px 0", minHeight: 28, maxHeight: 170, overflowY: "auto", position: "relative" }}>
                            {days.map((d, i) => <div key={ymd(d)} style={{ gridColumn: i + 1, gridRow: `1 / span ${Math.max(1, allDay.lanes)}`, borderLeft: line }} />)}
                            {allDay.bars.map((b) => (
                                <Chip key={b.it.key} it={b.it}
                                    style={{ gridColumn: `${b.s + 1} / ${b.e + 2}`, gridRow: b.lane + 1, margin: "0 3px 0 4px", alignSelf: "center" }} />
                            ))}
                        </div>
                    </div>
                    {/* hours */}
                    <div ref={scrollRef} style={{ maxHeight: 620, overflowY: "auto", position: "relative" }}>
                        <div style={{ display: "grid", gridTemplateColumns: colTemplate, height: 24 * HOUR, position: "relative" }}>
                            {/* hour labels */}
                            <div style={{ position: "relative" }}>
                                {Array.from({ length: 24 }).map((_, h) => (
                                    <div key={h} className="text-muted" style={{ position: "absolute", top: h * HOUR - 7, right: 6, fontSize: 10 }}>{hourLabel(h)}</div>
                                ))}
                            </div>
                            {days.map((d) => {
                                const key = ymd(d);
                                const isToday = sameDay(d, today);
                                const timed = timedLayout((byDay.get(key) || []).filter((it) => !it.allDay), mode === "week" ? 3 : 12);
                                return (
                                    <div key={key} style={{ position: "relative", borderLeft: line, background: isToday ? "var(--ssw-tint-blue, #f3f7ff)" : (d.getDay() === 0 || d.getDay() === 6) ? "var(--ssw-surface-2, #fafbfc)" : undefined }}>
                                        {Array.from({ length: 24 }).map((_, h) => (
                                            <div key={h} style={{ position: "absolute", top: h * HOUR, left: 0, right: 0, borderTop: line, height: 0 }} />
                                        ))}
                                        {timed.map((ev) => {
                                            const height = Math.max(20, (ev.endMin - ev.startMin) / 60 * HOUR - 2);
                                            if (ev.group) {
                                                const tip = ev.group.map((g) => `${fmtMin(g.startMin)} – ${fmtMin(g.endMin)}  ${g.title}`).join("\n");
                                                return (
                                                    <div key={`grp${ev.startMin}`} title={tip} onClick={() => openDay(d)}
                                                        style={{
                                                            ...chipBase("attendance", true), position: "absolute", top: ev.startMin / 60 * HOUR + 1, height,
                                                            left: 2, right: 2, whiteSpace: "normal", cursor: "pointer", zIndex: 1, boxShadow: "0 1px 2px rgba(0,0,0,.12)",
                                                        }}>
                                                        <div style={{ fontWeight: 600 }}>{ev.group.length} at the same time</div>
                                                        <div style={{ opacity: 0.8 }}>{fmtMin(ev.startMin)} – {fmtMin(ev.endMin)}</div>
                                                        {height >= 56 && <div style={{ opacity: 0.8 }}>Click to open the day</div>}
                                                    </div>
                                                );
                                            }
                                            // less than half a week column is too narrow for the full title; a Day
                                            // column is about seven times wider, so only very crowded days get dense there
                                            const narrow = (ev.span / ev.lanes) * (count === 1 ? 7 : 1) < 0.5;
                                            return (
                                                <Chip key={ev.it.key} it={ev.it} timed dense={narrow}
                                                    style={{
                                                        position: "absolute", top: ev.startMin / 60 * HOUR + 1, height,
                                                        left: `calc(${(ev.lane / ev.lanes) * 100}% + 2px)`, width: `calc(${(ev.span / ev.lanes) * 100}% - 4px)`,
                                                        // short blocks stay on one line; taller ones wrap at word ends and clip
                                                        // whole words at the edge (Google does the same), never mid-word
                                                        whiteSpace: height < 36 ? "nowrap" : "normal", wordBreak: "normal", overflowWrap: "normal",
                                                        boxShadow: "0 1px 2px rgba(0,0,0,.12)", zIndex: 1,
                                                    }} />
                                            );
                                        })}
                                        {isToday && (
                                            <div style={{ position: "absolute", top: nowMin / 60 * HOUR, left: 0, right: 0, height: 0, borderTop: "2px solid #e5484d", zIndex: 2 }}>
                                                <span style={{ position: "absolute", left: -5, top: -6, width: 10, height: 10, borderRadius: 5, background: "#e5484d" }} />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader title={title} subtitle={`My ${kind} schedule`} breadcrumbItems={breadcrumb} />
                {error && <Alert color="danger" className="mt-2">{error}</Alert>}

                <Card className="mt-3">
                    <CardBody>
                        {/* toolbar, Google style: Today ‹ › heading … view switch */}
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                            <Button color="light" size="sm" onClick={() => setCursor(dayOnly(new Date()))} disabled={isCurrent} style={{ borderRadius: 6 }}>Today</Button>
                            <Button color="light" size="sm" onClick={() => go(-1)} title="Previous" style={{ borderRadius: 999, width: 30, padding: 0 }}><i className="ri-arrow-left-s-line" /></Button>
                            <Button color="light" size="sm" onClick={() => go(1)} title="Next" style={{ borderRadius: 999, width: 30, padding: 0 }}><i className="ri-arrow-right-s-line" /></Button>
                            <h5 className="mb-0 ms-1" style={{ fontWeight: 500 }}>{heading}</h5>
                            {(loading || gcalLoading) && <span className="text-muted small ms-1"><i className="ri-loader-4-line" /> Loading…</span>}
                            <ButtonGroup size="sm" className="ms-auto">
                                {[["day", "Day"], ["week", "Week"], ["month", "Month"]].map(([m, label]) => (
                                    <Button key={m} color="primary" outline={mode !== m} onClick={() => pickMode(m)}>{label}</Button>
                                ))}
                            </ButtonGroup>
                        </div>
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-3 small">
                            <span className="text-muted me-1"><i className="ri-google-line me-1" />Show:</span>
                            {gcal ? (
                                <>
                                    <Pill on={show.mine} tone="mine" onClick={() => toggle("mine")}>My meetings</Pill>
                                    <Pill on={show.company} tone="company" onClick={() => toggle("company")}>Company events</Pill>
                                    <Pill on={show.out} tone="attendance" onClick={() => toggle("out")}>Colleagues out</Pill>
                                    <span className="text-muted ms-2">
                                        {mode === "month" ? "Month shows your own days and a grey \"N out\" count. Click a day to open it." : "Solid chips are your own; grey ones are colleagues' time off."}
                                    </span>
                                    {!gcal.personal && gcal.personalError && <span className="text-warning ms-2">{gcal.personalError}</span>}
                                </>
                            ) : (
                                <span className="text-muted">Your own {kind} only. {mode === "month" ? "Click a day to open it." : ""}</span>
                            )}
                        </div>

                        {mode === "month" ? renderMonth() : renderTimeGrid()}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export const FtoSchedule = () => (
    <ScheduleCalendar
        kind="FTO"
        title="My FTO Schedule"
        fetcher={getFtoDept}
        breadcrumb={[{ label: "Attendance", to: "/workflows?wf=pac" }, { label: "My FTO Schedule" }]}
    />
);
export const CoaSchedule = () => (
    <ScheduleCalendar
        kind="COA"
        title="My COA Schedule"
        fetcher={getCoaDept}
        breadcrumb={[{ label: "Attendance", to: "/workflows?wf=pac" }, { label: "My COA Schedule" }]}
    />
);
export default FtoSchedule;
